import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, filter, Subscription, timer } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { LanguageServiceService } from '../../services/language-service.service';
import { LoginService } from '../../components/Authentication/auth/login.service';
import { LogoutService } from '../../components/Authentication/auth/logout.service';
import { UserService } from '../../services/user';
import { ChatConversation, ChatService } from '../../services/chat';
import { JobApplicationService } from '../../services/job-application';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input() isActive!: boolean;

  currentLanguage: string | null = 'ar';
  token: string | null = null;

  contentReady: boolean = false;

  accountType: string = '';

  isAdmin: boolean = false;
  isSubAdmin: boolean = false;
  isEmployee: boolean = false;
  isDeliveryHero: boolean = false;
  isfinancialEmployee: boolean = false;

  showAllUsers: boolean = false;
  tokenExists: boolean = false;

  showPopup: boolean = false;
  unreadChatCount = 0;
  hasChatNotification = false;
  unreadApplicationCount = 0;
  hasApplicationNotification = false;

  private subscriptions = new Subscription();
  private chatPollingSub?: Subscription;
  private applicationPollingSub?: Subscription;
  private latestSeenChatMessageId: number | string | null = null;
  private knownApplicationIds = new Set<number | string>();
  private applicationNotificationsInitialized = false;
  private audioContext?: AudioContext;

  constructor(
    private translate: TranslateService,
    private _languageService: LanguageServiceService,
    private _LoginService: LoginService,
    private _logoutService: LogoutService,
    private _userService: UserService,
    private chatService: ChatService,
    private applicationService: JobApplicationService,
    private router: Router
  ) {
    this.translate.addLangs(['ar']);
    this.translate.setDefaultLang('ar');

    const savedLang = localStorage.getItem('language') || 'ar';
    this.currentLanguage = savedLang;
    this.translate.use(savedLang);
  }

  ngOnInit(): void {
    this.syncHeaderState();

    this.subscriptions.add(
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe(() => {
          this.syncHeaderState();
          this.clearChatNotificationIfViewingChats();
          this.clearApplicationNotificationIfViewingApplications();
        })
    );

    this.subscriptions.add(
      this._languageService.currentLanguage$.subscribe((language) => {
        this.currentLanguage = language;
      })
    );

    this.subscriptions.add(
      this._LoginService.token$.subscribe((token) => {
        this.tokenExists = !!token;

        if (token) {
          this.token = token;
          this.loadAccountType();
          this.startChatNotifications();
          this.startApplicationNotifications();
        } else {
          this.resetRoleFlags();
          this.stopChatNotifications();
          this.stopApplicationNotifications();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.stopChatNotifications();
    this.stopApplicationNotifications();
  }

  syncHeaderState(): void {
    this.token = localStorage.getItem('token');
    this.tokenExists = !!this.token;

    if (this.tokenExists) {
      this.loadAccountType();
      this.startChatNotifications();
      this.startApplicationNotifications();
    } else {
      this.resetRoleFlags();
      this.stopChatNotifications();
      this.stopApplicationNotifications();
    }
  }
loadAccountType(): void {
  this.resetRoleFlags();

  if (!localStorage.getItem('token')) {
    this.tokenExists = false;
    return;
  }

  this._userService.getAccountType().subscribe({
    next: (data) => {
      console.log('HEADER ACCOUNT TYPE RESPONSE:', data);

      // The API may return the numeric role in either `type` or `user_type`.
      // Resolve both so admins still receive their finance and employee navigation.
      const numericType = String(data['type'] ?? data['user_type'] ?? '').trim();
      const userType = String(data['user_type'] ?? '').trim().toLowerCase();

      this.accountType = numericType;

      if (numericType === '1' || userType === 'admin') {
        this.isAdmin = true;
        this.showAllUsers = true;
      }

      if (numericType === '6') {
        this.isSubAdmin = true;
      }

      if (numericType === '2') {
        this.isfinancialEmployee = true;
      }

      if (numericType === '7') {
        this.isEmployee = true;
      }

      console.log('HEADER FLAGS:', {
        accountType: this.accountType,
        userType,
        isAdmin: this.isAdmin,
        isSubAdmin: this.isSubAdmin,
        isfinancialEmployee: this.isfinancialEmployee,
        showAllUsers: this.showAllUsers,
      });
    },
    error: (error) => {
      console.error('HEADER ACCOUNT TYPE ERROR:', error);

      localStorage.clear();
      localStorage.setItem('language', 'ar');

      this.token = null;
      this.tokenExists = false;
      this.resetRoleFlags();
    },
  });
}  resetRoleFlags(): void {
    this.accountType = '';

    this.isAdmin = false;
    this.isSubAdmin = false;
    this.isEmployee = false;
    this.isDeliveryHero = false;
    this.isfinancialEmployee = false;

    this.showAllUsers = false;
  }

  toggleLanguage(): void {
    this.currentLanguage = 'ar';
    this.translate.use('ar');
    localStorage.setItem('language', 'ar');

    this._languageService.toggleLanguage();
  }

  openPopup(): void {
    this.showPopup = true;
  }

  closePopup(): void {
    this.showPopup = false;
  }

  help(): void {
    this.openPopup();
  }

  logout(): void {
    this.contentReady = true;

    this._logoutService.logout(this.token).subscribe({
      next: () => {
        localStorage.removeItem('token');
        localStorage.setItem('language', 'ar');

        this.token = null;
        this.tokenExists = false;
        this.resetRoleFlags();
        this.stopChatNotifications();
        this.stopApplicationNotifications();

        window.location.reload();
      },
      error: () => {
        this.contentReady = false;
      },
    });
  }

  openChats(): void {
    this.clearChatNotification();
    this.router.navigate(['/allchats']);
  }

  openApplications(): void {
    this.clearApplicationNotification();
    this.router.navigate(['/apply']);
  }

  private startChatNotifications(): void {
    if (this.chatPollingSub && !this.chatPollingSub.closed) {
      return;
    }

    this.chatPollingSub = timer(0, 8000)
      .pipe(
        switchMap(() =>
          this.chatService
            .getAdminConversations({
              per_page: 10,
              page: 1,
            })
            .pipe(catchError(() => EMPTY))
        )
      )
      .subscribe((res) => {
        this.handleChatNotificationSnapshot(res.data || []);
      });
  }

  private stopChatNotifications(): void {
    this.chatPollingSub?.unsubscribe();
    this.chatPollingSub = undefined;
    this.latestSeenChatMessageId = null;
    this.clearChatNotification();
  }

  private startApplicationNotifications(): void {
    if (this.applicationPollingSub && !this.applicationPollingSub.closed) {
      return;
    }

    this.applicationPollingSub = timer(0, 8000)
      .pipe(
        switchMap(() =>
          this.applicationService
            .getJobApplications({ page: 1, per_page: 50 })
            .pipe(catchError(() => EMPTY))
        )
      )
      .subscribe((res) => this.handleApplicationNotificationSnapshot(res));
  }

  private stopApplicationNotifications(): void {
    this.applicationPollingSub?.unsubscribe();
    this.applicationPollingSub = undefined;
    this.knownApplicationIds.clear();
    this.applicationNotificationsInitialized = false;
    this.clearApplicationNotification();
  }

  private handleApplicationNotificationSnapshot(res: any): void {
    const payload = res?.data ?? res?.applications ?? res;
    const applications = Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload)
        ? payload
        : [];

    const applicationIds = applications
      .map((application: any) => application?.id)
      .filter((id: number | string | null | undefined): id is number | string => id !== null && id !== undefined);

    if (!this.applicationNotificationsInitialized) {
      applicationIds.forEach((id: number | string) => this.knownApplicationIds.add(id));
      this.applicationNotificationsInitialized = true;
      return;
    }

    const newApplicationIds = applicationIds.filter((id: number | string) => !this.knownApplicationIds.has(id));
    applicationIds.forEach((id: number | string) => this.knownApplicationIds.add(id));

    if (newApplicationIds.length > 0) {
      this.unreadApplicationCount += newApplicationIds.length;
      this.hasApplicationNotification = true;

      if (!this.isViewingApplications()) {
        this.playChatNotificationSound();
      }
    }

    this.clearApplicationNotificationIfViewingApplications();
  }

  private handleChatNotificationSnapshot(conversations: ChatConversation[]): void {
    const waitingConversations = conversations.filter((conversation) => {
      return (
        conversation.status !== 'closed' &&
        conversation.latest_message?.sender_type !== 'admin'
      );
    });

    const latestConversation = waitingConversations[0];
    const latestMessageId = this.getLatestMessageIdentity(latestConversation);

    if (!this.latestSeenChatMessageId) {
      this.latestSeenChatMessageId = latestMessageId;
      this.unreadChatCount = waitingConversations.length;
      this.hasChatNotification = this.unreadChatCount > 0;
      this.clearChatNotificationIfViewingChats();
      return;
    }

    if (latestMessageId && latestMessageId !== this.latestSeenChatMessageId) {
      this.latestSeenChatMessageId = latestMessageId;
      this.unreadChatCount = Math.max(1, waitingConversations.length);
      this.hasChatNotification = true;

      if (!this.isViewingChats()) {
        this.playChatNotificationSound();
      }
    } else {
      this.unreadChatCount = waitingConversations.length;
      this.hasChatNotification = this.unreadChatCount > 0;
    }

    this.clearChatNotificationIfViewingChats();
  }

  private getLatestMessageIdentity(conversation?: ChatConversation): number | string | null {
    if (!conversation?.latest_message) {
      return null;
    }

    return (
      conversation.latest_message.id ||
      conversation.latest_message.created_at ||
      conversation.last_message_at ||
      conversation.id
    );
  }

  private clearChatNotificationIfViewingChats(): void {
    if (this.isViewingChats()) {
      this.clearChatNotification();
    }
  }

  private clearApplicationNotificationIfViewingApplications(): void {
    if (this.isViewingApplications()) {
      this.clearApplicationNotification();
    }
  }

  private isViewingChats(): boolean {
    return (
      this.router.url.startsWith('/allchats') ||
      this.router.url.startsWith('/conversation')
    );
  }

  private isViewingApplications(): boolean {
    return this.router.url.startsWith('/apply');
  }

  private clearChatNotification(): void {
    this.unreadChatCount = 0;
    this.hasChatNotification = false;
  }

  private clearApplicationNotification(): void {
    this.unreadApplicationCount = 0;
    this.hasApplicationNotification = false;
  }

  private playChatNotificationSound(): void {
    try {
      this.audioContext = this.audioContext || new window.AudioContext();

      const oscillator = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();
      const now = this.audioContext.currentTime;

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(740, now);
      oscillator.frequency.setValueAtTime(980, now + 0.08);

      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.16, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.26);

      oscillator.connect(gain);
      gain.connect(this.audioContext.destination);

      oscillator.start(now);
      oscillator.stop(now + 0.28);
    } catch {
      // The browser may block audio until the user interacts with the page.
    }
  }
}
